// API Configuration for consistent URL handling across environments.
// Single source of truth for the API base URL: RTK Query, token refresh and the WebSocket read this.
const getApiBaseUrl = () => {
  // Use environment variable if set
  const baseUrl = import.meta.env.VITE_API_URL;
  if (baseUrl) {
    // In production, force an absolute URL down to its path so requests inherit
    // the page's protocol+host (avoids mixed-content / CORS issues behind the proxy).
    if (import.meta.env.PROD && /^https?:\/\//.test(baseUrl)) {
      try {
        return new URL(baseUrl).pathname;
      } catch {
        return '/api/v1';
      }
    }
    return baseUrl;
  }

  // In development, connect directly to backend port
  if (import.meta.env.DEV) {
    return 'http://localhost:8001/api/v1';
  }

  // In production, use relative URL (behind proxy)
  return '/api/v1';
};

const getWebSocketUrl = () => {
  // Use environment variable if set
  if (import.meta.env.VITE_WS_URL) {
    return import.meta.env.VITE_WS_URL;
  }

  // In development, connect directly to backend WebSocket
  if (import.meta.env.DEV) {
    return 'ws://localhost:8001';
  }

  // In production, use current host with appropriate protocol
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
};

// Helper function to ensure URLs have proper trailing slashes
// FastAPI redirects URLs without trailing slashes to URLs with trailing slashes (307)
// This causes CORS issues, so we ensure all collection endpoints have trailing slashes
export const formatApiUrl = (url: string): string => {
  // Remove leading slash if present (will be added by baseUrl)
  url = url.replace(/^\//, '');

  // Collection endpoints (plural) should have trailing slashes
  const collectionEndpoints = ['devices'];
  const firstSegment = url.split('/')[0].split('?')[0]; // Get first path segment without query params

  if (collectionEndpoints.includes(firstSegment)) {
    // For collection endpoints, ensure trailing slash before query params
    const [path, query] = url.split('?');
    const pathWithSlash = path.endsWith('/') ? path : `${path}/`;
    return query ? `${pathWithSlash}?${query}` : pathWithSlash;
  }

  // Single resource endpoints should NOT have trailing slashes
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  WS_URL: getWebSocketUrl(),
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
};
